import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useParcels } from "../../context/ParcelContext";
import { formatAcre, formatCurrency, getHighlights, getLandUseColor, toTitleCase } from "../../utils/parcelFormatters";
import Badge from "./misc/Badge";
import Pagination from "./misc/Pagination";
import { exportParcelsToExcel } from "../../utils/excelParcelsModule";
import { Info } from "lucide-react";
import { Parcel } from "../../services/parcelTypes";

function SkeletonCard() {
	return (
		<div className="rounded-xl border p-4">
			<div className="flex items-start justify-between gap-4">
				<div className="flex-1 space-y-2">
					<div className="h-5 w-32 animate-pulse rounded-md bg-muted" />
					<div className="h-4 w-48 animate-pulse rounded-md bg-muted" />
				</div>
				<div className="space-y-1 text-right">
					<div className="h-3 w-16 animate-pulse rounded-md bg-muted" />
					<div className="h-5 w-24 animate-pulse rounded-md bg-muted" />
				</div>
			</div>
			<div className="mt-4 flex gap-2">
				<div className="h-6 w-20 animate-pulse rounded-md bg-muted" />
				<div className="h-6 w-16 animate-pulse rounded-md bg-muted" />
				<div className="h-6 w-18 animate-pulse rounded-md bg-muted" />
			</div>
		</div>
	);
}

export default function ParcelsTable() {
	const navigate = useNavigate();
	const { parcels, loading, error, total } = useParcels();
	const [searchParams, setSearchParams] = useSearchParams();
	const [showLandUseFilter, setShowLandUseFilter] = useState(false);
	const [landUseFilterQuery, setLandUseFilterQuery] = useState("");

	const pageSize = 10;
	const searchQuery = searchParams.get("q") ?? "";
	const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
	const sortBy = searchParams.get("sort") ?? "";
	const selectedLandUses = (searchParams.get("landUse") ?? "")
		.split(",")
		.map((value) => value.trim())
		.filter(Boolean);

	const landUseOptions = useMemo(() => {
		const values = new Set<string>();

		parcels.forEach((p) => {
			const landUse =
				p.landUse?.description?.trim() ??
				p.landUse?.category?.trim() ??
				"";

			if (landUse) values.add(landUse);
		});

		return Array.from(values).sort((a, b) => a.localeCompare(b));
	}, [parcels]);

	const filteredLandUseOptions = useMemo(() => {
		if (!landUseFilterQuery.trim()) return landUseOptions;

		const query = landUseFilterQuery.toLowerCase().trim();
		return landUseOptions.filter((option) =>
			option.toLowerCase().includes(query)
		);
	}, [landUseOptions, landUseFilterQuery]);

	const setPage = (nextPage: number) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set("page", String(nextPage));
			return next;
		});
	};

	const setQuery = (value: string) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);

			if (value.trim()) {
				next.set("q", value);
			} else {
				next.delete("q");
			}

			next.set("page", "1");
			return next;
		});
	};

	const setSortBy = (value: string) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);

			if (value) {
				next.set("sort", value);
			} else {
				next.delete("sort");
			}

			next.set("page", "1");
			return next;
		});
	};

	const toggleLandUse = (value: string) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			const current = (next.get("landUse") ?? "")
				.split(",")
				.map((item) => item.trim())
				.filter(Boolean);

			const updated = current.includes(value)
				? current.filter((item) => item !== value)
				: [...current, value].sort((a, b) => a.localeCompare(b));

			if (updated.length > 0) {
				next.set("landUse", updated.join(","));
			} else {
				next.delete("landUse");
			}

			next.set("page", "1");
			return next;
		});
	};

	const clearFilters = () => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.delete("sort");
			next.delete("landUse");
			next.set("page", "1");
			return next;
		});
	};

	const visibleParcels = useMemo(() => {
		let result = [...parcels];

		const isInvalidValue = (value?: string | null) =>
        !value || value === "N/A" || value === "—";

		const getDisplayName = (p: Parcel) => {
			const propertyName = p.ownership?.propertyName?.trim();
			if (!isInvalidValue(propertyName)) return propertyName!;
			return p.pin ?? "";
		};

		const startsWithLetter = (value: string) => /^[a-zA-Z]/.test(value);

		const getAppraisedTotal = (p: Parcel) =>
		p.valuation?.appraised?.total ?? null;

		const getCityLastContactTime = (p: Parcel) => {
			if (!p.cityLastContactDate) return null;

			const time = Date.parse(p.cityLastContactDate);
			return Number.isNaN(time) ? null : time;
		};

		const getLotSqft = (p: Parcel) => p.site?.lot?.sqft ?? null;

		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase().trim();

			result = result.filter((p) => {
				const pin = p.pin?.toLowerCase() ?? "";
				const propertyName = isInvalidValue(p.ownership?.propertyName)
					? ""
					: p.ownership!.propertyName!.toLowerCase();

				return pin.includes(query) || propertyName.includes(query);
			});
		}

		if (selectedLandUses.length > 0) {
			result = result.filter((p) => {
				const landUse =
					p.landUse?.description ??
					p.landUse?.category ??
					"—";

				return selectedLandUses.includes(landUse);
			});
		}

		result.sort((a, b) => {
			const nameA = getDisplayName(a);
			const nameB = getDisplayName(b);

			const aStartsLetter = startsWithLetter(nameA);
			const bStartsLetter = startsWithLetter(nameB);

			// Push non A–Z to bottom
			if (aStartsLetter && !bStartsLetter) return -1;
			if (!aStartsLetter && bStartsLetter) return 1;

			// Normal alphabetical within same group
			const nameCompare = nameA.localeCompare(nameB, undefined, {
				sensitivity: "base",
				numeric: true,
			});

			if (sortBy === "appraisedTotal_desc") {
				const aValue = getAppraisedTotal(a) ?? -1;
				const bValue = getAppraisedTotal(b) ?? -1;
				return bValue - aValue || nameCompare;
			}

			if (sortBy === "appraisedTotal_asc") {
				const aValue = getAppraisedTotal(a) ?? Number.MAX_SAFE_INTEGER;
				const bValue = getAppraisedTotal(b) ?? Number.MAX_SAFE_INTEGER;
				return aValue - bValue || nameCompare;
			}

			if (sortBy === "cityLastContact_desc") {
				const aTime = getCityLastContactTime(a) ?? -1;
				const bTime = getCityLastContactTime(b) ?? -1;
				return bTime - aTime || nameCompare;
			}

			if (sortBy === "cityLastContact_asc") {
				const aTime = getCityLastContactTime(a) ?? Number.MAX_SAFE_INTEGER;
				const bTime = getCityLastContactTime(b) ?? Number.MAX_SAFE_INTEGER;
				return aTime - bTime || nameCompare;
			}

			if (sortBy === "lotSqft_desc") {
				const aSqft = getLotSqft(a) ?? -1;
				const bSqft = getLotSqft(b) ?? -1;
				return bSqft - aSqft || nameCompare;
			}

			if (sortBy === "lotSqft_asc") {
				const aSqft = getLotSqft(a) ?? Number.MAX_SAFE_INTEGER;
				const bSqft = getLotSqft(b) ?? Number.MAX_SAFE_INTEGER;
				return aSqft - bSqft || nameCompare;
			}

			return nameCompare;
		});

		return result;
	}, [parcels, searchQuery, selectedLandUses, sortBy]);

	const pageCount = Math.max(1, Math.ceil((visibleParcels.length ?? 0) / pageSize));

	const pageItems = useMemo(() => {
		const start = (page - 1) * pageSize;
		return visibleParcels.slice(start, start + pageSize);
	}, [visibleParcels, page]);

	useEffect(() => {
		if (page > pageCount) {
			setPage(pageCount);
		}
	}, [page, pageCount, setPage]);

	const handleSelect = (pin: string) => {
		navigate(`/parcels/${encodeURIComponent(pin)}${window.location.search}`);
	};

	const clearLandUses = () => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.delete("landUse");
			next.set("page", "1");
			return next;
		});
	};

	const clearAll = () => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);

			next.delete("q");
			next.delete("landUse");
			next.delete("sort");
			next.set("page", "1");

			return next;
		});
	};

	return (
		<div className="w-full">
			{/* Header */}
			<div className="p-4 pt-8 mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between bg-[#21506F]">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#ffffff26]">
							<svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
							</svg>
						</div>
						<h2 className="text-xl text-white">Parcels</h2>
					</div>

					<button
						type="button"
						onClick={() => exportParcelsToExcel(visibleParcels)}
						className="inline-flex items-center gap-1 rounded-lg bg-[#ffffff26] px-3 py-2 text-xs text-white hover:bg-[#ffffff40] transition-colors"
					>
						<svg fill="white" aria-hidden="true" width={16} height={16} xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 28 28">
							<path stroke="none" d="M22.7,1.5c-.1-.2-.4-.3-.6-.2H5.1c-.2,0-.4,0-.6.2-.2.2-.2.4-.2.6v21.6c0,.1,0,.2,0,.3,0,.1,0,.2.2.3,0,0,.2.1.3.2.1,0,.2,0,.3,0h5.4l-2.3-2.8h2.6v-6h5.4v6h2.6l-2.2,2.8h5.4c.2,0,.4,0,.6-.2h0c.2-.1.3-.4.2-.6V2.1c0-.2-.1-.4-.3-.6ZM10.4,14.4h-4.9v-3h4.9v3ZM10.4,10.3h-4.9v-3.1h4.9v3.1ZM10.4,6h-4.9v-3.1h4.9v3.1ZM16.1,14.4h-4.9v-3h4.9v3ZM16.1,10.3h-4.9v-3.1h4.9v3.1ZM16.1,6h-4.9v-3.1h4.9v3.1ZM21.7,14.5h-4.9v-3.1h4.9v3.1ZM21.7,10.3h-4.9v-3.2h4.9v3.2ZM21.7,6.1h-4.9v-3.2h4.9v3.2Z"></path>
							<polygon points="16.8 22.7 13.6 26.7 10.4 22.7 11.9 22.7 11.9 16.7 15.3 16.7 15.3 22.7 16.8 22.7"></polygon>
						</svg>
						Download
					</button>
				</div>
				<p className="text-xs text-[#ffffff99]">Select a parcel to view and edit details</p>
				<div className="bg-[#ffffff26] px-2 py-1 rounded-full w-fit">
					<p className="text-xs text-[rgba(255,255,255,0.9)]">
						{loading
							? "Loading..."
							: searchQuery || selectedLandUses.length > 0 || sortBy
								? `${visibleParcels.length} of ${total ?? parcels.length} records`
								: `${total ?? parcels.length} records`}
					</p>
				</div>
			</div>
			<div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 bg-white">
				{/* Search Input */}
				<div className="relative w-full sm:w-72">
					<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
						<svg className="h-4 w-4 text-black/80" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
						</svg>
					</div>
					<input
						type="text"
						placeholder="Search by property name or parcel ID..."
						value={searchQuery}
						onChange={(e) => setQuery(e.target.value)}
						className="w-full rounded-lg border  bg-[#F4F6F9] py-2 pl-10 pr-4 text-sm text-black placeholder:text-black/60 focus:border-[#21506F] focus:outline-none focus:ring-2 focus:ring-[#21506F]/20"
					/>
					{searchQuery && (
						<button
							onClick={() => setQuery("")}
							className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
						>
							<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					)}
				</div>
				<div className="mt-4 mb-2 pb-4 border-b-[#E2E8F0]">
					<div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
						<div className="relative flex-1">
							<div className="flex items-center gap-1.5">
								<label className="label">Land use</label>

								<div className="group relative flex items-start -mt-0.5" tabIndex={0}>
									<Info className="h-3 w-3 cursor-pointer text-muted-foreground" />

									<div className="pointer-events-none absolute left-full top-1/2 z-10 ml-2 w-56 -translate-y-1/2 rounded-md bg-gray-900 px-2 py-1.5 text-xs text-white opacity-0 shadow-md transition-opacity group-hover:opacity-100 group-focus:opacity-100">
										Filter parcels by one or more land use categories.
									</div>
								</div>
							</div>

							<button
								type="button"
								onClick={() => setShowLandUseFilter((prev) => !prev)}
								className="mt-2 inline-flex w-full items-center justify-between rounded-lg border bg-[#F4F6F9] px-3 py-2 text-sm text-black/80 transition-colors hover:bg-accent lg:max-w-md"
							>
								<span className="truncate">
									{selectedLandUses.length === 0
										? "All land uses"
										: selectedLandUses.length === 1
											? selectedLandUses[0]
											: `${selectedLandUses.length} land uses selected`}
								</span>
								<svg
									className={`h-4 w-4 text-black/80 transition-transform ${showLandUseFilter ? "rotate-180" : ""
										}`}
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={2}
									stroke="currentColor"
								>
									<path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25L12 15.75 4.5 8.25" />
								</svg>
							</button>

							{showLandUseFilter && (
								<div className="mt-3 w-full rounded-xl border bg-white p-3 shadow-sm lg:max-w-md">
									<div className="relative">
										<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
											<svg
												className="h-4 w-4 text-black/80"
												fill="none"
												viewBox="0 0 24 24"
												strokeWidth={2}
												stroke="currentColor"
											>
												<path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
											</svg>
										</div>
										<input
											type="text"
											placeholder="Search land use..."
											value={landUseFilterQuery}
											onChange={(e) => setLandUseFilterQuery(e.target.value)}
											className="w-full rounded-lg border py-2 pl-10 pr-4 bg-[#F4F6F9] text-sm text-black/80 placeholder:text-black/60 focus:border-[#21506F] focus:outline-none focus:ring-2 focus:ring-[#21506F]/20"
										/>
									</div>

									<div className="mt-3 max-h-72 space-y-1 overflow-y-auto pr-1">
										{filteredLandUseOptions.length === 0 ? (
											<div className="px-1 text-sm text-muted-foreground">
												No land use categories match "{landUseFilterQuery}".
											</div>
										) : (
											filteredLandUseOptions.map((option) => {
												const isSelected = selectedLandUses.includes(option);

												return (
													<label
														key={option}
														className="flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-accent"
													>
														<input
															type="checkbox"
															checked={isSelected}
															onChange={() => toggleLandUse(option)}
															className="mt-0.5 h-4 w-4 rounded  text-[#21506F] focus:ring-[#21506F]/20"
														/>
														<span className="text-sm text-foreground">{option}</span>
													</label>
												);
											})
										)}
									</div>

									<div className="mt-3 flex items-center justify-between border-t  pt-3">
										<button
											type="button"
											onClick={() => {
												clearLandUses();
												setLandUseFilterQuery("");
											}}
											disabled={selectedLandUses.length === 0}
											className="rounded-lg border bg-[#E8E8E8] px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
										>
											Clear land use
										</button>

										<button
											type="button"
											onClick={() => setShowLandUseFilter(false)}
											className="rounded-lg bg-[#21506F] px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-[#21506F]/90"
										>
											Done
										</button>
									</div>
								</div>
							)}
						</div>

						<div className="w-full lg:w-72">
							<div className="flex items-center gap-1.5">
								<label className="label">Sort By</label>

								<div className="group relative flex items-start" tabIndex={0}>
									<Info className="h-3 w-3 cursor-pointer text-muted-foreground" />

									<div className="pointer-events-none absolute left-full top-1/2 z-10 ml-2 w-56 -translate-y-1/2 rounded-md bg-gray-900 px-2 py-1.5 text-xs text-white opacity-0 shadow-md transition-opacity group-hover:opacity-100 group-focus:opacity-100">
										Order results alphabetically, by appraised total value, by last city contact date, or by lot square footage.
									</div>
								</div>
							</div>

							<select
								value={sortBy}
								onChange={(e) => setSortBy(e.target.value)}
								className="mt-2 w-full rounded-lg border bg-[#F4F6F9] px-3 py-2 text-sm text-black/80 focus:border-[#21506F] focus:outline-none focus:ring-2 focus:ring-[#21506F]/20"
							>
								<option value="">Default (alphabetical)</option>
								<option value="appraisedTotal_desc">Appraised total value: Highest first</option>
								<option value="appraisedTotal_asc">Appraised total value: Lowest first</option>
								<option value="cityLastContact_desc">Last city contact: Newest first</option>
								<option value="cityLastContact_asc">Last city contact: Oldest first</option>
								<option value="lotSqft_desc">Lot square footage: Largest first</option>
								<option value="lotSqft_asc">Lot square footage: Smallest first</option>
							</select>
						</div>
					</div>

					{(selectedLandUses.length > 0 || sortBy) && (
						<div className="mt-4 flex flex-wrap items-center gap-2 border-t  pt-4">
							{selectedLandUses.map((landUse) => (
								<span
									key={landUse}
									className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs font-medium text-foreground"
								>
									{landUse}
									<button
										type="button"
										onClick={() => toggleLandUse(landUse)}
										className="text-muted-foreground hover:text-foreground"
										aria-label={`Remove ${landUse} filter`}
									>
										×
									</button>
								</span>
							))}

							{sortBy && (
								<span className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs font-medium text-foreground">
									<span className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs font-medium text-foreground">
										{sortBy === "appraisedTotal_desc" && "Highest appraised value first"}
										{sortBy === "appraisedTotal_asc" && "Lowest appraised value first"}
										{sortBy === "cityLastContact_desc" && "Newest city contact first"}
										{sortBy === "cityLastContact_asc" && "Oldest city contact first"}
										{sortBy === "lotSqft_desc" && "Largest lot first"}
										{sortBy === "lotSqft_asc" && "Smallest lot first"}
									</span>
								</span>
							)}

							<button
								type="button"
								onClick={clearFilters}
								className="ml-auto rounded-lg border  bg-background px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
							>
								Clear filters
							</button>
						</div>
					)}
				</div>
			</div>

			{/* Error State */}
			{error && (
				<div className="mb-4 flex items-start gap-3 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
					<svg className="mt-0.5 h-5 w-5 text-destructive" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
					</svg>
					<div>
						<p className="text-sm font-medium text-destructive">Error loading parcels</p>
						<p className="mt-1 text-sm text-destructive/80">{error}</p>
					</div>
				</div>
			)}

			{/* Cards List */}
			<div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
				<p className="divider-label">
					{loading
						? "Loading..."
						: searchQuery || selectedLandUses.length > 0 || sortBy
							? `${visibleParcels.length} of ${total ?? parcels.length} records`
							: `Showing ${total ?? parcels.length} of ${total}`}
				</p>
				<div className="space-y-3">
					{loading ? (
						<>
							<SkeletonCard />
							<SkeletonCard />
							<SkeletonCard />
						</>
					) : (
						pageItems.map((p) => {
							const { pin, primaryDisplay, landUse, landUseCategory, acres, appraisedTotal } = getHighlights(p);

							const landUseColor = getLandUseColor(landUseCategory);

							return (
								<button
									key={pin}
									onClick={() => handleSelect(pin)}
									className="group relative w-full overflow-hidden rounded-xl border border-[#E2E8F0] border-l-4 bg-white p-4 text-left shadow-sm transition-all hover:border-primary/30 hover:bg-accent/50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
									style={{ borderLeftColor: landUseColor }}
								>
									<div className="flex items-start justify-between gap-4">
										<div className="min-w-0 flex-1">
											<div className="flex items-center gap-2">
												<span className="card-name">
													{toTitleCase(primaryDisplay.value)}
												</span>
												<svg
													className="h-4 w-4 flex-shrink-0 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100"
													fill="none"
													viewBox="0 0 24 24"
													strokeWidth={2}
													stroke="currentColor"
												>
													<path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
												</svg>
											</div>
											<p className="card-id">{pin}</p>
										</div>

										<div className="flex-shrink-0 text-right">
											<p className="card-value-label">Appraised</p>
											<p className="card-value-num">
												{formatCurrency(appraisedTotal)}
											</p>
										</div>
									</div>

									<div className="mt-2 flex flex-wrap items-center gap-2">
										<Badge variant="landUse" color={landUseColor}>{landUseCategory}</Badge>
										<Badge variant="landUseDescription">{landUse ?? "—"}</Badge>
										<Badge variant="acres">{formatAcre(acres)}</Badge>

									</div>
								</button>
							);
						})
					)}
				</div>
			</div>

			{/* Empty State */}
			{!loading && visibleParcels.length === 0 && !error && (
				<div className="flex flex-col items-center justify-center rounded-xl border border-dashed  py-12">
					<div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
						<svg className="h-6 w-6 text-muted-foreground" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
						</svg>
					</div>
					<p className="mt-4 text-sm font-medium text-foreground">
						{searchQuery || selectedLandUses.length > 0 ? "No matching parcels" : "No parcels found"}
					</p>
					<p className="mt-1 text-sm text-muted-foreground">
						{searchQuery
							? `No results for "${searchQuery}"`
							: selectedLandUses.length > 0 || sortBy
								? "Try adjusting your filters or sort"
								: "Try adjusting your search criteria"}
					</p>
					{(searchQuery || selectedLandUses.length > 0 || sortBy) && (
						<button
							onClick={clearAll}
							className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#21506F] px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-[#21506F]/90"
						>
							Clear search and filters
						</button>
					)}
				</div>
			)}

			{/* Pagination */}
			{!loading && visibleParcels.length > 0 && (
				<div className="mt-6">
					<Pagination
						page={page}
						pageCount={pageCount}
						onPrev={() => setPage(Math.max(1, page - 1))}
						onNext={() => setPage(Math.min(pageCount, page + 1))}
					/>
				</div>
			)}
		</div>
	);
}
